/**
 * Pre-game / app-shell navigation finite state machine.
 */

export type AppFlowPhase =
    | 'splash'
    | 'start'
    | 'intro'
    | 'configurator'
    | 'countdown'
    | 'run'

export type AppFlowEvent =
    | { type: 'SPLASH_CONTINUE' }
    | { type: 'FLOW_ADVANCE' }
    | { type: 'FLOW_GO_TO_START' }
    | { type: 'COUNTDOWN_COMPLETE' }
    | { type: 'CAMERA_INTRO_COMPLETE' }
    | { type: 'DEBUG_RUN' }
    | { type: 'RETURN_TO_MENU' }
    | { type: 'RETURN_TO_MENU_CONFIGURATOR' }
    | { type: 'RESTART_TO_COUNTDOWN' }

export type AppFlowContext = {
    //True when assets and Strapi cache are ready
    canContinueFromSplash: boolean
    tutorialEnabled: boolean
}

export type AppFlowEffect = 'enterMenu' | 'startNormal' | 'startDebug'

export type AppFlowTransition = {
    nextPhase: AppFlowPhase
    effects: AppFlowEffect[]
}

const advanceMap: Partial<Record<AppFlowPhase, AppFlowPhase>> = {
    start: 'configurator',
    configurator: 'intro',
    intro: 'countdown',
}

/**
 * Compute the next phase and side effects. Returns `null` if the event is
 * invalid for the current phase or context (no-op).
 */
export function resolveAppFlowTransition(
    phase: AppFlowPhase,
    event: AppFlowEvent,
    ctx: AppFlowContext,
): AppFlowTransition | null {
    if (event.type === 'RETURN_TO_MENU_CONFIGURATOR') {
        return { nextPhase: 'configurator', effects: ['enterMenu'] }
    }

    if (event.type === 'RETURN_TO_MENU') {
        return { nextPhase: 'start', effects: ['enterMenu'] }
    }

    if (event.type === 'RESTART_TO_COUNTDOWN') {
        if (phase !== 'run') {
            return null
        }

        return { nextPhase: 'countdown', effects: [] }
    }

    if (event.type === 'DEBUG_RUN') {
        return { nextPhase: 'start', effects: ['startDebug'] }
    }

    if (event.type === 'COUNTDOWN_COMPLETE') {
        if (phase !== 'countdown') {
            return null
        }

        return {
            nextPhase: 'countdown',
            effects: [],
        }
    }

    if (event.type === 'CAMERA_INTRO_COMPLETE') {
        if (phase !== 'countdown') {
            return null
        }

        return {
            nextPhase: 'run',
            effects: [],
        }
    }



    if (event.type === 'SPLASH_CONTINUE') {
        if (phase !== 'splash' || !ctx.canContinueFromSplash) {
            return null
        }
        return { nextPhase: 'start', effects: [] }
    }

    if (event.type === 'FLOW_ADVANCE') {
        if (phase === 'configurator' && !ctx.tutorialEnabled) {
            return { nextPhase: 'countdown', effects: [] }
        }

        const next = advanceMap[phase]

        if (!next) {
            return null
        }

        return {
            nextPhase: next,
            effects: [],
        }
    }

    if (event.type === 'FLOW_GO_TO_START') {
        if (phase !== 'configurator') {
            return null
        }

        return { nextPhase: 'start', effects: [] }
    }

    return null
}
